import { del, get, patch, post, put } from './api';
import { api } from './api';

// ─── Dashboard ──────────────────────────────
export type DashboardPayload = {
  today: { count: number; revenue: number; avgCheck: number };
  yesterday: { count: number; revenue: number; avgCheck: number };
  week: { count: number; revenue: number };
  month: { count: number; revenue: number };
  // Полученные деньги за сегодня (PAID, без отменённых) — «выручка = касса».
  // today.revenue остаётся оборотом по созданным заказам (вкл. неоплаченные).
  // Опционально: старый бэк это поле не отдаёт — фронт делает fallback на today.
  received?: { total: number; ordersCount: number; avgCheck: number };
  deltas: { ordersCount: number; revenue: number; avgCheck: number };
  paymentMethods: Array<{ method: string; count: number; amount: number }>;
  appOrders: { count: number; revenue: number };
  topDishes: Array<{
    id: string;
    name: string;
    quantity: number;
    total: number;
    cost?: number;
    profit?: number;
  }>;
  activeDishes: number;
  totalClients: number;
  // Прибыль за сегодня и за месяц: оборот минус закупка минус расходы.
  // Старый бэк поля не отдаёт — фронт просто не рисует блок.
  profit?: { today: ProfitBlock; month: ProfitBlock };
  now: string;
};

export type ProfitBlock = {
  revenue: number;
  cogs: number;
  grossProfit: number;
  marginPercent: number;
  foodCostPercent: number;
  // Доля продаж (в деньгах), где себестоимость задана. Ниже 95% — маржа неполная.
  costCoverage: number;
  expenses: number;
  netProfit: number;
};
export const getDashboard = () => get<DashboardPayload>('/api/admin/dashboard');

// ─── Current shift (live) ────────────────────
export type CurrentShiftOrder = {
  id: string;
  number: number;
  status: 'NEW' | 'IN_PROGRESS' | 'READY' | 'COMPLETED' | 'CANCELLED';
  source: 'CASH' | 'APP';
  total: number;
  createdAt: string;
  table: { id: string; number: number; area: string } | null;
  client: { id: string; name: string | null; phone: string | null } | null;
};

export type ShiftPayout = {
  id: string;
  supplierName: string | null;
  comment: string | null;
  paidAmount: number;
  createdAt: string;
  items: Array<{ name: string; unit: string; quantity: number; costPerUnit: number; lineTotal: number }>;
};

// Расход заведения, проведённый через кассу смены (KASSA_CASH / KASSA_TRANSFER).
export type ShiftExpense = {
  id: string;
  amount: number;
  method: ExpenseMethod;
  methodLabel: string | null;
  comment: string | null;
  createdAt: string;
  category: { id: string; name: string; color: string | null } | null;
  user: { id: string; name: string | null } | null;
  /** Владелец внёс расход уже после закрытия смены — на бумажном Z-отчёте его нет. */
  addedAfterClose?: boolean;
};

// Позиция, снятая кассиром с заказа до оплаты: нажал «−» (DECREASED) либо убрал
// строку целиком (REMOVED). Журнал ведёт бэкенд, сверяя состав заказа при синке.
export type ShiftItemChange = {
  id: string;
  createdAt: string;
  kind: 'DECREASED' | 'REMOVED';
  orderId: string;
  orderNumber: number | null;
  tableNumber: number | null;
  cashierName: string | null;
  deviceName: string | null;
  dishName: string;
  qtyBefore: number;
  qtyAfter: number;
  unitPrice: number;
  amount: number;
  /** Снятие с телефона официанта: причина, обязательный комментарий, кто снял. Старый бэк не отдаёт. */
  reason?: 'NO_WRITE_OFF' | 'WRITE_OFF' | null;
  writeOffPayer?: 'ESTABLISHMENT' | 'WAITER' | null;
  comment?: string | null;
  actorName?: string | null;
  chargedWaiterName?: string | null;
};

/** Сколько приняли официанты с телефона: наличные им ещё сдавать в кассу. Копейки. */
export type WaiterCollection = {
  waiterId: string | null;
  name: string;
  cash: number;
  cashless: number;
  count: number;
};

export type CurrentShiftPayload = {
  now: string;
  shift:
    | null
    | {
        id: string;
        number: number;
        status: 'OPEN' | 'CLOSED';
        openedAt: string;
        durationMin: number;
        cashOnOpen: number;
        expectedCash?: number;
        notes: string | null;
        // Имя, которое кассир ввёл при открытии смены. Планшет логинится общим
        // device-юзером, поэтому cashier.name — владелец учётки, а не человек.
        cashierName?: string | null;
        cashier: {
          id: string;
          name: string | null;
          phone: string | null;
          email: string | null;
        } | null;
      };
  totals?: {
    ordersCount: number;
    revenue: number;
    avgCheck: number;
    discountTotal: number;
    paidOrdersCount: number;
    cancelledCount: number;
    cashOnHand: number;
    cashPayouts?: number;
    // Расходы заведения, проведённые через кассу смены (этап «Расходы»).
    cashExpenses?: number;
    transferExpenses?: number;
    // Прибыль смены: продажи её заказов минус себестоимость минус расходы смены.
    // salesRevenue — сумма позиций заказов смены (база для маржи; отличается от
    // revenue, который считается по полученным деньгам).
    salesRevenue?: number;
    cogs?: number;
    grossProfit?: number;
    marginPercent?: number;
    foodCostPercent?: number;
    costCoverage?: number;
    netProfit?: number;
    // Снято с заказов до оплаты: строк, штук и сумма.
    removedItemsCount?: number;
    removedItemsQty?: number;
    removedItemsAmount?: number;
  };
  statuses?: Array<{ status: CurrentShiftOrder['status']; count: number; revenue: number }>;
  paymentMethods?: Array<{ method: string; count: number; amount: number }>;
  sources?: Array<{ source: 'CASH' | 'APP'; count: number; revenue: number }>;
  topDishes?: Array<{
    id: string;
    name: string;
    quantity: number;
    total: number;
    cost?: number;
    profit?: number;
  }>;
  hourly?: Array<{ hour: number; count: number; revenue: number }>;
  activeOrders?: CurrentShiftOrder[];
  recentOrders?: CurrentShiftOrder[];
  payouts?: ShiftPayout[];
  expenses?: ShiftExpense[];
  itemChanges?: ShiftItemChange[];
  waiterCollections?: WaiterCollection[];
};

export const getCurrentShift = () => get<CurrentShiftPayload>('/api/admin/current-shift');

// ─── Analytics (extended) ───────────────────
export type AnalyticsPeriod = 'today' | '7d' | '30d' | '90d';

export type AnalyticsPayload = {
  period: AnalyticsPeriod;
  range: { from: string; to: string };
  summary: {
    ordersCount: number;
    revenue: number;
    avgCheck: number;
    paidOrders: number;
    cancelledOrders: number;
    discountTotal: number;
    uniqueClients: number;
    // Расходы за период и остаток оборота после них. Старый бэк их не отдаёт.
    expenses?: number;
    net?: number;
    // Себестоимость проданного и маржа. costCoverage — доля продаж (в деньгах),
    // где себестоимость задана: пока она низкая, маржа неполная.
    cogs?: number;
    grossProfit?: number;
    marginPercent?: number;
    foodCostPercent?: number;
    costCoverage?: number;
    netProfit?: number;
  };
  deltas: {
    ordersCount: number;
    revenue: number;
    avgCheck: number;
    cancelledOrders: number;
    expenses?: number;
    grossProfit?: number;
  };
  expenses?: {
    total: number;
    count: number;
    fromKassa: number;
    byCategory: Array<{ id: string | null; name: string; color: string | null; total: number; count: number }>;
    byMethod: Array<{ method: ExpenseMethod; total: number; count: number }>;
    daily: Array<{ date: string; total: number }>;
  };
  daily: Array<{ date: string; revenue: number; orders: number; expenses?: number }>;
  sources: Array<{ source: 'CASH' | 'APP'; count: number; revenue: number }>;
  paymentMethods: Array<{ method: string; count: number; amount: number }>;
  hourly: Array<{ hour: number; count: number; revenue: number }>;
  statuses: Array<{ status: string; count: number; revenue: number }>;
  topDishes: Array<{
    id: string;
    name: string;
    quantity: number;
    total: number;
    cost?: number;
    profit?: number;
  }>;
  topCategories: Array<{ id: string; name: string; quantity: number; revenue: number }>;
  avgCheckDynamic: Array<{ date: string; avgCheck: number }>;
};

export const getAnalytics = (params: { period?: AnalyticsPeriod; from?: string; to?: string } = {}) => {
  const query: Record<string, string> = {};
  if (params.from && params.to) {
    query.from = params.from;
    query.to = params.to;
  } else {
    query.period = params.period ?? '30d';
  }
  return get<AnalyticsPayload>('/api/admin/analytics', query);
};

// ─── Establishments ─────────────────────────
export type EstablishmentTheme = {
  primary?: string | null;
  fontFamily?: string | null;
  faviconUrl?: string | null;
  iconUrl?: string | null;
};

export type Establishment = {
  id: string;
  name: string;
  logoUrl: string | null;
  address: string | null;
  phone: string | null;
  // Город и координаты для публичной карты на лендинге. Заполняются из
  // подсказок DaData при выборе адреса (см. suggestAddress ниже).
  city: string | null;
  geoLat: number | null;
  geoLon: number | null;
  showOnPublicMap: boolean;
  domain: string | null;
  theme: EstablishmentTheme | null;
  timezone: string;
  currency: string;
  createdAt: string;
  // ── Карточка в гостевом приложении (people), страница «Приложение» ──
  // Показывать заведение в приложении. Не влияет на карту лендинга и QR-меню.
  showInApp: boolean;
  // Короткое описание под названием (до 300 символов).
  description: string | null;
  // Обложки по порядку (первая — главная). Пусто → фото блюд автоматически.
  covers: string[];
  // «Выбор гостей» вручную (id блюд). Пусто → топ по заказам за 30 дней.
  featuredDishIds: string[];
  // Публичная ссылка менюборда для ТВ (п.15), собирает сервер из PUBLIC_MENU_URL.
  menuBoardUrl?: string;
  // settings.* — фича-флаги заведения (read).
  settings?: {
    supplierStore?: { enabled?: boolean } | null;
    // Скрыть выручку/итоги смены от кассиров в кассе.
    hideRevenueFromCashier?: boolean | null;
    // Лимит скидки кассира на кассе, % (п.6). null/нет — берётся с пользователя.
    cashierMaxDiscountPercent?: number | null;
    // Банковский терминал (док 28-ekvayring.md).
    acquiring?: AcquiringSettings | null;
    // Менюборд на ТВ (п.15).
    menuBoard?: MenuBoardSettings | null;
  } | null;
  // Write-only поля для PATCH: тумблеры фича-флагов.
  supplierStore?: { enabled: boolean };
  hideRevenueFromCashier?: boolean;
  cashierMaxDiscountPercent?: number | null;
  acquiring?: AcquiringSettings;
  menuBoard?: MenuBoardSettings;
};

// Эквайринг: как касса общается с банковским терминалом (док 28-ekvayring.md).
//   none    — кнопки «Карта» нет, только наличные/перевод/QR;
//   manual  — кассир проводит оплату на терминале руками, касса лишь фиксирует;
//   inpas   — Inpas Dual Connector (Т-Банк, Альфа, Райф, ВТБ, ПСБ) по HTTP/TCP;
//   sber    — Сбер UPOS / TTK по TCP;
//   arcus   — Arcus 2 (ГПБ, Уралсиб, Открытие);
//   sbp_qr  — СБП QR через интернет-API банка.
export type AcquiringProvider = 'none' | 'manual' | 'inpas' | 'sber' | 'arcus' | 'sbp_qr';
export type AcquiringSettings = {
  provider: AcquiringProvider;
  host?: string | null;
  port?: number | null;
  terminalId?: string | null;
  timeoutSec?: number | null;
  printSlip?: boolean;
};
export const acquiringProviderLabels: Record<AcquiringProvider, string> = {
  none: 'Без эквайринга',
  manual: 'Вручную (терминал не подключён к кассе)',
  inpas: 'Inpas Dual Connector — Т-Банк, Альфа, Райффайзен, ВТБ, ПСБ',
  sber: 'Сбер UPOS / TTK',
  arcus: 'Arcus 2 — Газпромбанк, Уралсиб, Открытие',
  sbp_qr: 'СБП QR (интернет-API банка)',
};

// Менюборд на телевизор (п.15): публичная страница menu-app /board/:id.
export type MenuBoardSettings = {
  enabled: boolean;
  categoryIds?: string[];
  rotateSec?: number | null;
  showPhotos?: boolean;
  showPrices?: boolean;
  title?: string | null;
};
export const listEstablishments = () => get<Establishment[]>('/api/admin/establishments');
export const createEstablishment = (b: Partial<Establishment>) =>
  post<Establishment>('/api/admin/establishments', b);
export const updateEstablishment = (id: string, b: Partial<Establishment>) =>
  patch<Establishment>(`/api/admin/establishments/${id}`, b);
export const deleteEstablishment = (id: string) =>
  del<{ ok: true }>(`/api/admin/establishments/${id}`);

// ─── Карточка в гостевом приложении: кухни ───
// Справочник ведёт оператор (sadmin) — владелец только отмечает свои (до 10).
export type AppCuisine = {
  id: string;
  slug: string;
  name: string;
  imageUrl: string | null;
};
export const listAppCuisines = () =>
  get<{ cuisines: AppCuisine[] }>('/api/admin/app/cuisines').then((r) => r.cuisines);
export const getEstablishmentCuisines = (estId: string) =>
  get<{ cuisineIds: string[] }>(`/api/admin/establishments/${estId}/cuisines`).then((r) => r.cuisineIds);
export const setEstablishmentCuisines = (estId: string, cuisineIds: string[]) =>
  put<{ cuisineIds: string[] }>(`/api/admin/establishments/${estId}/cuisines`, { cuisineIds }).then(
    (r) => r.cuisineIds,
  );

// Подсказки адреса. Идут через наш бэкенд, а не напрямую в DaData, чтобы ключ
// не попал в бандл админки. precise = координаты дома, а не центра улицы.
export type AddressSuggestion = {
  value: string;
  city: string | null;
  lat: number | null;
  lon: number | null;
  precise: boolean;
};
export const suggestAddress = (query: string) =>
  get<{ suggestions: AddressSuggestion[] }>('/api/admin/geo/suggest-address', { query });

export type PaymentSetting = {
  id: string;
  method: string;
  isEnabled: boolean;
  qrImageUrl: string | null;
  details: Record<string, unknown>;
};
export const listPaymentSettings = (estId: string) =>
  get<PaymentSetting[]>(`/api/admin/establishments/${estId}/payments`);
export const upsertPaymentSetting = (estId: string, b: Partial<PaymentSetting>) =>
  post(`/api/admin/establishments/${estId}/payments`, b);

// Реквизиты ЮKassa заведения: онлайн-оплата гостя приходит на счёт ресторана.
// Секретный ключ сервер наружу не отдаёт — только маска последних символов.
export type YukassaStatus = {
  isConfigured: boolean;
  shopId: string | null;
  secretKeyMask: string | null;
  mode: 'test' | 'live' | null;
  updatedAt: string | null;
  platformFallback: boolean;
  canConfigure: boolean;
};
export const getYukassaSettings = (estId: string) =>
  get<YukassaStatus>(`/api/admin/establishments/${estId}/yukassa`);
export const saveYukassaSettings = (estId: string, b: { shopId: string; secretKey: string }) =>
  put<YukassaStatus>(`/api/admin/establishments/${estId}/yukassa`, b);
export const deleteYukassaSettings = (estId: string) =>
  del<YukassaStatus>(`/api/admin/establishments/${estId}/yukassa`);

// ─── Users ─────────────────────────────────
export type AdminUser = {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  role: 'CLIENT' | 'CASHIER' | 'ADMIN' | 'OWNER' | 'WAITER';
  status: 'ACTIVE' | 'BLOCKED' | 'DELETED';
  establishment: { id: string; name: string } | null;
  maxDiscountPercent?: number;
  lastLoginAt: string | null;
  createdAt: string;
};
// Без known_roles бэкенд не отдаёт официантов: их не умеют показывать старые
// сборки iOS-админки. Здесь перечисляем все роли, которые знает эта страница.
const KNOWN_USER_ROLES: AdminUser['role'][] = ['CLIENT', 'CASHIER', 'ADMIN', 'OWNER', 'WAITER'];
export const listUsers = (q?: { role?: string; q?: string; status?: string }) =>
  get<{ results: AdminUser[]; stats: Array<{ role: string; status: string; _count: number }> }>(
    '/api/admin/users',
    { ...q, known_roles: KNOWN_USER_ROLES.join(',') },
  );
export const createUser = (b: Partial<AdminUser> & { password?: string }) =>
  post<AdminUser>('/api/admin/users', b);
export const updateUser = (id: string, b: Partial<AdminUser> & { password?: string }) =>
  patch<AdminUser>(`/api/admin/users/${id}`, b);
export const deleteUser = (id: string) => del(`/api/admin/users/${id}`);
export const setUserStatus = (id: string, status: 'ACTIVE' | 'BLOCKED') =>
  post(`/api/admin/users/${id}/status`, { status });
export const resetUserPassword = (id: string, password: string) =>
  post(`/api/admin/users/${id}/password`, { password });

// ─── Menu ──────────────────────────────────
export type Category = {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  visibleInMenu: boolean;
  // Скидка на всю категорию, % (п.2). 0 — нет. Применяется к каждому блюду
  // категории поверх акционной цены блюда; кассы и QR-меню видят уже сниженную цену.
  discountPercent?: number;
};
export const listCategories = () => get<Category[]>('/api/admin/menu/categories');
export const createCategory = (b: Partial<Category>) => post<Category>('/api/admin/menu/categories', b);
export const updateCategory = (id: string, b: Partial<Category>) =>
  patch<Category>(`/api/admin/menu/categories/${id}`, b);
// movedArchivedDishes — сколько удалённых ранее блюд пришлось увести в «Архив»
// (они держат ссылки из истории заказов и мешали удалить категорию).
export const deleteCategory = (id: string) =>
  del<{ ok: true; movedArchivedDishes: number }>(`/api/admin/menu/categories/${id}`);

// Разделы приготовления заведения: «Кухня», «Бар», «Кальянная» — любые.
// legacyTag связывает раздел со старым enum: по нему кассы, которые ещё не
// обновились, получают привычные kitchen/bar.
export type MenuSection = {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  legacyTag: 'KITCHEN' | 'BAR' | 'NONE' | null;
  _count?: { dishes: number };
};
export type MenuSectionInput = {
  name?: string;
  sortOrder?: number;
  isActive?: boolean;
  legacyTag?: 'KITCHEN' | 'BAR' | 'NONE' | null;
};
export const listSections = () => get<MenuSection[]>('/api/admin/menu/sections');
export const createSection = (b: MenuSectionInput) =>
  post<MenuSection>('/api/admin/menu/sections', b);
export const updateSection = (id: string, b: MenuSectionInput) =>
  patch<MenuSection>(`/api/admin/menu/sections/${id}`, b);
export const deleteSection = (id: string) =>
  del<{ ok: true; movedDishes: number }>(`/api/admin/menu/sections/${id}`);

export type Dish = {
  id: string;
  categoryId: string;
  name: string;
  description: string | null;
  price: number;
  discountPrice: number | null;
  /** Раздел приготовления из справочника (null — «Без раздела»). */
  sectionId: string | null;
  // Закупочная цена порции (копейки). null — не задана: себестоимость возьмётся
  // из тех-карты склада, если она есть.
  costPrice: number | null;
  weight: string | null;
  section: 'KITCHEN' | 'BAR' | 'NONE';
  isAvailable: boolean;
  sortOrder: number;
  photoUrl: string | null;
  photos: string[];
  modifiers: Array<{ id: string; name: string; price: number }>;
};
export type DishInput = Partial<Omit<Dish, 'photos'>> & { photos?: string[] };
export const listDishes = (params?: { categoryId?: string; q?: string }) =>
  get<Dish[]>('/api/admin/menu/dishes', params);
export const createDish = (b: DishInput) => post<Dish>('/api/admin/menu/dishes', b);
export const updateDish = (id: string, b: DishInput) =>
  patch<Dish>(`/api/admin/menu/dishes/${id}`, b);
// archived=true — блюдо продавалось, поэтому не удалено, а скрыто из меню:
// позиции прошлых заказов на него ссылаются.
export const deleteDish = (id: string) =>
  del<{ ok: true; archived: boolean; soldItems: number; message?: string }>(
    `/api/admin/menu/dishes/${id}`,
  );

export type Modifier = {
  id: string;
  name: string;
  price: number;
  isActive: boolean;
  /** Строк в тех-карте модификатора и стоимость одного выбора (копейки). */
  techCardItems?: number;
  costPerUnit?: number;
};
export const listModifiers = () => get<Modifier[]>('/api/admin/menu/modifiers');

// Тех-карта модификатора: расход на ОДИН выбор модификатора в порции.
export type ModifierTechCard = {
  modifierId: string;
  modifierName: string;
  modifierPrice: number;
  costPerUnit: number;
  items: Array<{
    id: string;
    stockItemId: string;
    quantity: number;
    stockItem: { id: string; name: string; unit: string; costPerUnit: number; kind: StockItemKind };
  }>;
};
export const getModifierTechCard = (modifierId: string) =>
  get<ModifierTechCard>(`/api/admin/stock/modifiers/${modifierId}/tech-card`);
export const saveModifierTechCard = (
  modifierId: string,
  items: Array<{ stockItemId: string; quantity: number }>,
) => put<ModifierTechCard>(`/api/admin/stock/modifiers/${modifierId}/tech-card`, { items });
export const createModifier = (b: Partial<Modifier>) => post<Modifier>('/api/admin/menu/modifiers', b);
export const updateModifier = (id: string, b: Partial<Modifier>) =>
  patch<Modifier>(`/api/admin/menu/modifiers/${id}`, b);
export const deleteModifier = (id: string) => del(`/api/admin/menu/modifiers/${id}`);

// ─── Orders ────────────────────────────────
export type OrderListItem = {
  id: string;
  number: number;
  status: string;
  source: string;
  total: number;
  createdAt: string;
  // false = заказ из закрытой смены, удалять нельзя (только отмена). См. бэкенд deleteOrderAdmin.
  canDelete: boolean;
  table: { id: string; number: number; area: string } | null;
  client: { id: string; name: string | null; phone: string | null } | null;
};
export const listOrders = (q?: {
  status?: string;
  source?: string;
  dateFrom?: string;
  dateTo?: string;
  q?: string;
}) => get<OrderListItem[]>('/api/admin/orders', q);
export type OrderPaymentRow = {
  id?: string;
  method: string;
  amount: number;
  status: string;
  createdAt?: string;
};
export type OrderDetail = {
  id: string;
  number: number;
  status: string;
  source: string;
  total: number;
  subtotal: number;
  discount: number;
  createdAt: string;
  comment: string | null;
  deliveryAddress: string | null;
  clientPhone: string | null;
  table: { number: number; area: string } | null;
  client: { id?: string; name: string | null; phone: string | null } | null;
  // Смена, в которой закрыт заказ (п.1): менять способ оплаты можно только
  // в открытой смене — иначе Z-отчёт уже сведён.
  shift?: { id: string; number: number; status: string } | null;
  items: Array<{
    id: string;
    name: string;
    quantity: number;
    unitPrice: number;
    total: number;
    modifiers: Array<{ name: string; price?: number }>;
    comment: string | null;
  }>;
  payments: OrderPaymentRow[];
};
export const getOrder = (id: string) => get<OrderDetail>(`/api/admin/orders/${id}`);
export const cancelOrder = (id: string) => post(`/api/admin/orders/${id}/cancel`);
// п.1: изменить способ оплаты у оплаченного заказа. Один способ — `method`,
// смешанная — `split` (копейки по способам; сумма должна равняться оплаченной).
export type ChangePaymentBody = {
  method?: 'cash' | 'card' | 'transfer' | 'qrcode';
  split?: { cash?: number; card?: number; transfer?: number; qrcode?: number };
};
export const changeOrderPaymentMethod = (id: string, b: ChangePaymentBody) =>
  patch<OrderDetail>(`/api/admin/orders/${id}/payment-method`, b);

// ─── Права ролей (п.17) ───
export type PermissionRole = 'ADMIN' | 'CASHIER' | 'WAITER';
export type PermissionsMatrix = {
  /** roles — для каких ролей право имеет смысл. Старый бэк не отдаёт: тогда для всех. */
  codes: Array<{ code: string; title: string; group: string; roles?: PermissionRole[] }>;
  defaults: Partial<Record<PermissionRole, string[]>>;
  roles: Partial<Record<PermissionRole, { permissions: string[]; customized: boolean }>>;
};
export const getPermissions = () => get<PermissionsMatrix>('/api/admin/permissions');
// null для роли — сброс на дефолт.
export const setPermissions = (b: Partial<Record<PermissionRole, string[] | null>>) =>
  put<PermissionsMatrix>('/api/admin/permissions', b);
export const deleteOrder = (id: string) =>
  del<{ id: string; number: number; deleted: boolean }>(`/api/admin/orders/${id}`);

// ─── Shifts ────────────────────────────────
export type ShiftRow = {
  id: string;
  number: number;
  status: 'OPEN' | 'CLOSED';
  openedAt: string;
  closedAt: string | null;
  cashierName?: string | null;
  cashier: { id: string; name: string | null } | null;
  totalRevenue: number;
  ordersCount: number;
  /** Факт − ожидание по наличным у закрытой смены. Старый бэк не отдаёт. */
  cashDiff?: number | null;
};
export const listShifts = (q?: {
  dateFrom?: string;
  dateTo?: string;
  cashierId?: string;
  status?: 'OPEN' | 'CLOSED';
  limit?: number;
}) => get<ShiftRow[]>('/api/admin/shifts', q);

export type ShiftDetail = {
  id: string;
  number: number;
  status: 'OPEN' | 'CLOSED';
  openedAt: string;
  closedAt: string | null;
  durationMin: number;
  notes: string | null;
  cashOnOpen: number;
  cashOnClose: number | null;
  expectedCash: number;
  cash: {
    opening: number;
    cashIn: number;
    cashOut: number;
    nonCashIn: number;
    cashPayouts: number;
    transferPayouts?: number;
    cashExpenses?: number;
    transferExpenses?: number;
    expected: number;
    actual: number | null;
    diff: number | null;
    cashInCount: number;
    cashOutCount: number;
    nonCashInCount: number;
    cashPayoutsCount: number;
    cashExpensesCount?: number;
    transferExpensesCount?: number;
  };
  cashMovements: Array<{
    id: string;
    createdAt: string;
    method: 'CASH' | 'CARD' | 'QRCODE' | 'TRANSFER' | 'ONLINE' | 'MIXED';
    status: 'PAID' | 'REFUNDED' | 'PENDING' | 'FAILED';
    amount: number;
    direction: 'IN' | 'OUT';
    isCash: boolean;
    orderId: string;
    orderNumber: number | null;
    orderStatus: 'NEW' | 'IN_PROGRESS' | 'READY' | 'COMPLETED' | 'CANCELLED' | null;
    kind?: 'PAYMENT' | 'SUPPLY' | 'EXPENSE';
    note?: string | null;
    /** Оплату принял официант с телефона — наличные у него, а не в ящике. */
    acceptedBy?: string | null;
  }>;
  waiterCollections?: WaiterCollection[];
  payouts?: ShiftPayout[];
  expenses?: ShiftExpense[];
  itemChanges?: ShiftItemChange[];
  establishment: {
    id: string;
    name: string;
    address: string | null;
    phone: string | null;
    /** IANA-зона заведения (п.19): время в PDF считаем по ней. Старый бэк не отдаёт. */
    timezone?: string;
  } | null;
  cashierName?: string | null;
  cashier: {
    id: string;
    name: string | null;
    phone: string | null;
    email: string | null;
  } | null;
  totalRevenue: number;
  ordersCount: number;
  totals: {
    ordersCount: number;
    revenue: number;
    avgCheck: number;
    subtotal: number;
    discountTotal: number;
    paidOrdersCount: number;
    cancelledCount: number;
    cancelledTotal: number;
    // Снято с заказов до оплаты: строк, штук и сумма. Старый бэк их не отдаёт.
    removedItemsCount?: number;
    removedItemsQty?: number;
    removedItemsAmount?: number;
  };
  paymentBreakdown: Array<{ method: string; amount: number; count: number }>;
  /** Legacy-форма (3 ключа). Оставлена для обратной совместимости. */
  sections: Record<'KITCHEN' | 'BAR' | 'NONE', { quantity: number; total: number }>;
  /** Разделы заведения как есть — включая кастомные («Кальянная», «Цех»). */
  sectionRows?: Array<{ name: string; quantity: number; total: number }>;
  sources: Array<{ source: 'CASH' | 'APP'; count: number; revenue: number }>;
  statuses: Array<{
    status: 'NEW' | 'IN_PROGRESS' | 'READY' | 'COMPLETED' | 'CANCELLED';
    count: number;
    revenue: number;
  }>;
  topDishes: Array<{ id: string; name: string; quantity: number; total: number }>;
  topCategories: Array<{ id: string; name: string; quantity: number; revenue: number }>;
  hours: Array<{ hour: number; count: number; revenue: number }>;
  orders: Array<{
    id: string;
    number: number;
    status: 'NEW' | 'IN_PROGRESS' | 'READY' | 'COMPLETED' | 'CANCELLED';
    source: 'CASH' | 'APP';
    subtotal: number;
    discount: number;
    total: number;
    createdAt: string;
    table: { id: string; number: number; area: string } | null;
    client: { id: string; name: string | null; phone: string | null } | null;
  }>;
};

export const getShift = (id: string) => get<ShiftDetail>(`/api/admin/shifts/${id}`);

// ─── Expenses ──────────────────────────────
// Способы KASSA_* проводятся через открытую смену и уменьшают её кассу;
// остальные — общий расход заведения (только аналитика).
export type ExpenseMethod = 'KASSA_CASH' | 'KASSA_TRANSFER' | 'CASH' | 'CARD' | 'OTHER';

export const EXPENSE_METHOD_LABELS: Record<ExpenseMethod, string> = {
  KASSA_CASH: 'С кассы (наличные)',
  KASSA_TRANSFER: 'С кассы (перевод)',
  CASH: 'Наличные',
  CARD: 'Карта',
  OTHER: 'Другой способ',
};

export const isKassaExpense = (m: ExpenseMethod) => m === 'KASSA_CASH' || m === 'KASSA_TRANSFER';

export type ExpenseCategory = {
  id: string;
  name: string;
  color: string | null;
  sortOrder: number;
  isActive: boolean;
  expensesCount: number;
  createdAt: string;
};

export type Expense = {
  id: string;
  amount: number; // копейки
  method: ExpenseMethod;
  methodLabel: string | null;
  comment: string | null;
  spentAt: string;
  createdAt: string;
  category: { id: string; name: string; color: string | null } | null;
  user: { id: string; name: string | null } | null;
  shift: { id: string; number: number; status: 'OPEN' | 'CLOSED'; closedAt?: string | null } | null;
  // Был в смене на момент её закрытия: сумму, способ и дату менять нельзя.
  locked: boolean;
  // Внесён в уже закрытую смену (поправка владельца): можно править и удалить.
  addedAfterClose?: boolean;
};

export type ExpensesPayload = {
  items: Expense[];
  summary: {
    count: number;
    total: number;
    fromKassa: number;
    kassaCash: number;
    other: number;
    byMethod: Array<{ method: ExpenseMethod; total: number; count: number }>;
    byCategory: Array<{ id: string | null; name: string; color: string | null; total: number; count: number }>;
  };
  hasMore: boolean;
};

export type ExpenseInput = {
  amount: number; // копейки
  method: ExpenseMethod;
  methodLabel?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  comment?: string | null;
  spentAt?: string | null;
  /** Смена кассового расхода: открытая или уже закрытая. Не задана — открытая. */
  shiftId?: string | null;
};

export const listExpenses = (q?: {
  from?: string;
  to?: string;
  categoryId?: string;
  method?: ExpenseMethod;
  shiftId?: string;
  kassaOnly?: boolean;
  q?: string;
  limit?: number;
  offset?: number;
}) => get<ExpensesPayload>('/api/admin/expenses', q);
export const createExpense = (b: ExpenseInput) => post<Expense>('/api/admin/expenses', b);
export const updateExpense = (id: string, b: Partial<ExpenseInput>) =>
  patch<Expense>(`/api/admin/expenses/${id}`, b);
export const deleteExpense = (id: string) => del<{ ok: true }>(`/api/admin/expenses/${id}`);
export const listExpenseMethodLabels = () => get<string[]>('/api/admin/expenses/method-labels');

export const listExpenseCategories = (q?: { includeInactive?: boolean }) =>
  get<ExpenseCategory[]>('/api/admin/expense-categories', q);
export const createExpenseCategory = (b: { name: string; color?: string | null; sortOrder?: number }) =>
  post<ExpenseCategory>('/api/admin/expense-categories', b);
export const updateExpenseCategory = (
  id: string,
  b: { name?: string; color?: string | null; sortOrder?: number; isActive?: boolean },
) => patch<ExpenseCategory>(`/api/admin/expense-categories/${id}`, b);
export const deleteExpenseCategory = (id: string) =>
  del<{ ok: true }>(`/api/admin/expense-categories/${id}`);
export const seedExpenseCategories = () =>
  post<ExpenseCategory[]>('/api/admin/expense-categories/defaults', {});

// ─── Stock ─────────────────────────────────
export type StockItem = {
  id: string;
  name: string;
  unit: string;
  quantity: number;
  minQuantity: number;
  costPerUnit: number;
  sku?: string | null;
  // Фасовка закупки: «упаковка» по 1 л. Остатки/минимум/цена — в базовых
  // единицах (unit), упаковки нужны только для ввода (см. utils/packaging.ts).
  packLabel?: string | null;
  packSize?: number | null;
  linkedDishes?: number;
  /** GOODS — закупаемый товар, PREP — заготовка со своим рецептом. */
  kind?: StockItemKind;
  /** Выход рецепта заготовки: сколько базовых единиц даёт один прогон. */
  outputQuantity?: number | null;
  /** Процент отхода заготовки (очистка, уварка): эффективный выход меньше на него. */
  wastePercent?: number;
  /** Вести остаток заготовки или списывать её сырьём при продаже. */
  trackStock?: boolean;
  recipeItems?: number;
  usedInPreps?: number;
};
export type StockItemKind = 'GOODS' | 'PREP';
export const listStock = (q?: { q?: string; lowOnly?: boolean }) =>
  get<StockItem[]>('/api/admin/stock/items', q);
export const createStock = (b: Partial<StockItem>) => post<StockItem>('/api/admin/stock/items', b);
export const updateStock = (id: string, b: Partial<StockItem>) =>
  patch<StockItem>(`/api/admin/stock/items/${id}`, b);
export const deleteStock = (id: string) => del(`/api/admin/stock/items/${id}`);

// Движения
export type StockMovementType = 'ARRIVAL' | 'CONSUMPTION' | 'WRITE_OFF' | 'INVENTORY' | 'SALE';
/** Откуда движение: продажа, возврат при отмене, поставка, инвентаризация, кассир, админка. */
export type StockMovementSource = 'SALE' | 'RETURN' | 'SUPPLY' | 'INVENTORY' | 'KASSA' | 'ADMIN';
export const STOCK_SOURCE_LABELS: Record<StockMovementSource, string> = {
  SALE: 'Продажи',
  RETURN: 'Возвраты при отмене',
  SUPPLY: 'Поставки',
  INVENTORY: 'Инвентаризация',
  KASSA: 'Списания кассиров',
  ADMIN: 'Ручные операции',
};
export type WriteOffReason = 'SPOILAGE' | 'EXPIRED' | 'BREAKAGE' | 'STAFF' | 'TASTING' | 'OTHER';
export const WRITE_OFF_REASON_LABELS: Record<WriteOffReason, string> = {
  SPOILAGE: 'Порча',
  EXPIRED: 'Истёк срок',
  BREAKAGE: 'Бой / брак',
  STAFF: 'Питание персонала',
  TASTING: 'Проработка',
  OTHER: 'Другое',
};
export type StockMovement = {
  id: string;
  type: StockMovementType;
  quantity: number;
  costPerUnit: number;
  /** Для старых клиентов у списания кассиром сюда дописаны «кто» и причина. */
  comment: string | null;
  createdAt: string;
  stockItem: { id: string; name: string; unit: string };
  // Новые поля — старый бэк их не отдаёт.
  /** Комментарий как ввели, без подписей. */
  note?: string | null;
  source?: StockMovementSource;
  sourceLabel?: string;
  /** Кроме причин из формы списания: ORDER_REMOVAL — снято с заказа, RESET — сброс остатка. */
  reason?: WriteOffReason | 'ORDER_REMOVAL' | 'RESET' | null;
  reasonLabel?: string | null;
  actorName?: string | null;
  shiftNumber?: number | null;
  orderNumber?: number | null;
  wasteQuantity?: number | null;
  /** Списание отменено в админке: товар вернулся на склад, запись — зачёркнутая. */
  cancelledAt?: string | null;
  cancelledByName?: string | null;
  cancelComment?: string | null;
  /** Инвентаризация или сброс остатка, после которых отменить списание уже нельзя. */
  cancelBlockedBy?: StockCancelBlocker | null;
};
export type StockCancelBlocker =
  | { kind: 'INVENTORY'; number: number; at: string }
  | { kind: 'RESET'; at: string };
/** Причина движения «Сброс остатка» (тип INVENTORY): обнуление на вкладке «Позиции». */
export const STOCK_RESET_REASON = 'RESET' as const;
export const listStockMovements = (q?: {
  type?: StockMovementType;
  stockItemId?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
  source?: StockMovementSource;
  q?: string;
  /** Показать и отменённые списания (по умолчанию сервер их не отдаёт). */
  withCancelled?: boolean;
}) => get<StockMovement[]>('/api/admin/stock/movements', q);
/** Отмена ошибочного списания: количество возвращается на склад. */
export const cancelStockWriteOff = (id: string, comment?: string) =>
  post<{
    id: string;
    returned: number;
    stockItem: { id: string; name: string; unit: string; quantity: number };
  }>(`/api/admin/stock/movements/${id}/cancel`, { comment });
/** Сброс остатков всех позиций до нуля (только владелец). value — стоимость обнулённого, коп. */
export const resetStock = (comment?: string) =>
  post<{ count: number; value: number; negative: number }>('/api/admin/stock/reset', { comment });
export const createStockMovement = (b: {
  stockItemId: string;
  type: 'ARRIVAL' | 'CONSUMPTION' | 'WRITE_OFF';
  quantity: number;
  costPerUnit?: number;
  comment?: string;
  reason?: WriteOffReason;
}) => post<StockMovement>('/api/admin/stock/movements', b);

// Аналитика склада: все движения за период в рублях (копейки).
export type StockAnalytics = {
  from: string;
  to: string;
  timezone: string;
  summary: {
    arrival: number;
    returns: number;
    sales: number;
    writeOff: number;
    writeOffKassa: number;
    writeOffAdmin: number;
    consumption: number;
    inventorySurplus: number;
    inventoryShortage: number;
    /** Сброс остатка (обнуление владельцем) — отдельно от излишков и недостачи. Старый бэк не отдаёт. */
    resetIn?: number;
    resetOut?: number;
    waste: number;
    inCost: number;
    outCost: number;
    movements: number;
  };
  byDay: Array<{ date: string; inCost: number; outCost: number; salesCost: number; writeOffCost: number }>;
  byItem: Array<{
    id: string;
    name: string;
    unit: string;
    kind: StockItemKind;
    costPerUnit: number;
    opening: number;
    arrival: number;
    returns: number;
    sales: number;
    writeOff: number;
    consumption: number;
    inventory: number;
    /** Часть inventory со сброса остатка. Старый бэк не отдаёт. */
    reset?: number;
    closing: number;
    inCost: number;
    outCost: number;
    writeOffCost: number;
    wasteCost: number;
  }>;
  writeOffReasons: Array<{ reason: string; label: string; count: number; cost: number }>;
};
export const getStockAnalytics = (q: { from?: string; to?: string }) =>
  get<StockAnalytics>('/api/admin/stock/analytics', q);

// Тех-карты
export type TechCard = {
  id: string;
  dishId: string;
  stockItemId: string;
  quantity: number;
  dish: { id: string; name: string; price: number };
  stockItem: { id: string; name: string; unit: string; costPerUnit: number };
};
// Тех-карта одного блюда: строки, себестоимость порции (копейки) и контекст,
// нужный редактору — масштаб раскладки и ручная цена, которая её перебивает.
export type DishTechCardResult = {
  dishId: string;
  dishName?: string;
  dishPrice: number;
  /** Ручная закупочная цена блюда: если задана, в аналитике приоритет у неё. */
  manualCostPrice?: number | null;
  /** На сколько порций владелец вводил раскладку (в items — всегда на одну). */
  portions?: number | null;
  costPerPortion: number;
  items: TechCard[];
};
export const listTechCards = () => get<TechCard[]>('/api/admin/stock/tech-cards');
export const upsertTechCard = (b: { dishId: string; stockItemId: string; quantity: number }) =>
  post<TechCard>('/api/admin/stock/tech-cards', b);
export const deleteTechCard = (id: string) => del(`/api/admin/stock/tech-cards/${id}`);
// Полное сохранение тех-карты блюда одним атомарным запросом: набор ингредиентов
// заменяется целиком (пустой items[] очищает карту).
export const saveDishTechCard = (
  dishId: string,
  items: Array<{ stockItemId: string; quantity: number }>,
  portions?: number | null,
) => put<DishTechCardResult>(`/api/admin/stock/dishes/${dishId}/tech-card`, { items, portions });
export const getDishTechCard = (dishId: string) =>
  get<DishTechCardResult>(`/api/admin/stock/dishes/${dishId}/tech-card`);
export const copyDishTechCard = (dishId: string, sourceDishId: string) =>
  post<DishTechCardResult>(
    `/api/admin/stock/dishes/${dishId}/tech-card/copy-from/${sourceDishId}`,
    {},
  );

// ─── Заготовки (полуфабрикаты) ───────────────────────────────────────────
// Заготовка — позиция склада со своим рецептом: её себестоимость считается из
// состава, поэтому подорожавший ингредиент сразу поднимает цену всех блюд с ней.
export type PrepRecipeRow = {
  id?: string;
  componentId: string;
  quantity: number;
  component: { id: string; name: string; unit: string; costPerUnit: number; kind: StockItemKind };
};
export type Prep = {
  id: string;
  name: string;
  unit: string;
  quantity: number;
  costPerUnit: number;
  outputQuantity: number | null;
  /** Процент отхода и выход с его учётом. Старый бэк не отдаёт. */
  wastePercent?: number;
  effectiveOutput?: number;
  trackStock: boolean;
  recipe: PrepRecipeRow[];
  usedInDishes: Array<{ id: string; name: string }>;
  usedInPreps: Array<{ id: string; name: string }>;
};
export type PrepRecipe = {
  id: string;
  name: string;
  unit: string;
  kind: StockItemKind;
  costPerUnit: number;
  outputQuantity: number | null;
  wastePercent?: number;
  effectiveOutput?: number;
  trackStock: boolean;
  items: PrepRecipeRow[];
};
export const listPreps = () => get<Prep[]>('/api/admin/stock/preps');
export const getPrepRecipe = (itemId: string) =>
  get<PrepRecipe>(`/api/admin/stock/items/${itemId}/recipe`);
export const savePrepRecipe = (
  itemId: string,
  body: {
    items: Array<{ componentId: string; quantity: number }>;
    outputQuantity?: number;
    trackStock?: boolean;
    wastePercent?: number;
  },
) => put<PrepRecipe>(`/api/admin/stock/items/${itemId}/recipe`, body);
export const setStockItemKind = (itemId: string, kind: StockItemKind) =>
  post<{ ok: true; kind: StockItemKind }>(`/api/admin/stock/items/${itemId}/kind`, { kind });

// Инвентаризация
export type InventoryActShort = {
  id: string;
  number: number;
  status: 'DRAFT' | 'COMMITTED';
  comment: string | null;
  createdAt: string;
  committedAt: string | null;
  itemsCount: number;
  totalDeltaCost: number;
};
export type InventoryActItem = {
  id: string;
  stockItemId: string;
  expected: number;
  actual: number;
  delta: number;
  costPerUnit: number;
  comment: string | null;
  stockItem?: { id: string; name: string; unit: string };
};
export type InventoryActFull = {
  id: string;
  number: number;
  status: 'DRAFT' | 'COMMITTED';
  comment: string | null;
  createdAt: string;
  committedAt: string | null;
  items: InventoryActItem[];
};
export const listInventoryActs = () => get<InventoryActShort[]>('/api/admin/stock/inventory');
export const getInventoryAct = (id: string) =>
  get<InventoryActFull>(`/api/admin/stock/inventory/${id}`);
export const createInventoryAct = (b: {
  comment?: string;
  items: Array<{ stockItemId: string; actual: number }>;
}) => post<InventoryActFull>('/api/admin/stock/inventory', b);
export const commitInventoryAct = (id: string) =>
  post<InventoryActFull>(`/api/admin/stock/inventory/${id}/commit`);
export const deleteInventoryAct = (id: string) =>
  del(`/api/admin/stock/inventory/${id}`);

// Поставки (приёмка товара + оплата из кассы смены)
export type SupplyPaymentSource = 'NONE' | 'KASSA_CASH' | 'KASSA_TRANSFER' | 'EXTERNAL';
export type SupplyItem = {
  id: string;
  stockItemId: string;
  name: string;
  unit: string;
  packLabel?: string | null;
  packSize?: number | null;
  quantity: number;
  costPerUnit: number;
  lineTotal: number;
};
export type Supply = {
  id: string;
  supplierName: string | null;
  comment: string | null;
  totalCost: number;
  paymentSource: SupplyPaymentSource;
  paidAmount: number;
  shiftId: string | null;
  createdAt: string;
  // Архив прячет накладную из списка; приход и выплата остаются проведёнными.
  archivedAt: string | null;
  // Состав и оплата заперты: оплачено из закрытой смены либо это приёмка заказа
  // из «Закупа». Поставщика с комментарием можно править и тогда.
  locked: boolean;
  lockedReason: string | null;
  itemsCount: number;
  items: SupplyItem[];
};
// quantity/costPerUnit — в базовых единицах позиции; packLabel/packSize
// задают фасовку только для НОВОЙ позиции (создаётся приёмкой).
export type SupplyItemInput = {
  stockItemId?: string;
  name?: string;
  unit?: string;
  packLabel?: string | null;
  packSize?: number | null;
  quantity: number;
  costPerUnit: number;
};
// archived=1 отдаёт архив вместо активных накладных.
export const listSupplies = (q?: { limit?: number; archived?: boolean }) =>
  get<Supply[]>('/api/admin/stock/supplies', q);
export const countArchivedSupplies = () =>
  get<{ archived: number }>('/api/admin/stock/supplies/archived-count');
export const getSupply = (id: string) => get<Supply>(`/api/admin/stock/supplies/${id}`);
export const createSupply = (b: {
  supplierName?: string;
  comment?: string;
  paymentSource: SupplyPaymentSource;
  items: SupplyItemInput[];
}) => post<Supply>('/api/admin/stock/supplies', b);
// Состав заменяется целиком: склад откатывается и проводится заново.
export const updateSupply = (
  id: string,
  b: {
    supplierName?: string | null;
    comment?: string | null;
    paymentSource?: SupplyPaymentSource;
    items?: SupplyItemInput[];
  },
) => patch<Supply>(`/api/admin/stock/supplies/${id}`, b);
// Архивация доступна и для запертых накладных: склад и касса не двигаются.
export const archiveSupply = (id: string) =>
  post<Supply>(`/api/admin/stock/supplies/${id}/archive`, {});
export const unarchiveSupply = (id: string) =>
  post<Supply>(`/api/admin/stock/supplies/${id}/unarchive`, {});
export const deleteSupply = (id: string) => del(`/api/admin/stock/supplies/${id}`);

// ─── Reports ────────────────────────────────
export type ReportDishRow = {
  dishId: string;
  name: string;
  categoryId: string | null;
  categoryName: string;
  section: 'KITCHEN' | 'BAR' | 'NONE';
  /** Название раздела на момент продажи (для кастомных разделов). */
  sectionLabel?: string;
  quantity: number;
  total: number;
};
export type ReportCategory = {
  categoryId: string | null;
  categoryName: string;
  quantity: number;
  total: number;
  dishes: ReportDishRow[];
};
export type ReportSection = {
  section: 'KITCHEN' | 'BAR' | 'NONE';
  /** Название раздела заведения — по нему группируется отчёт. */
  sectionLabel: string;
  sectionSort?: number;
  quantity: number;
  total: number;
  categories: ReportCategory[];
};
export type SalesReport = {
  range: { from: string; to: string };
  totals: { ordersCount: number; itemsQuantity: number; revenue: number };
  sections: ReportSection[];
  top: ReportDishRow[];
  establishment: { id: string; name: string } | null;
  generatedAt: string;
};
export const getSalesReport = (q?: { from?: string; to?: string }) =>
  get<SalesReport>('/api/admin/reports/sales', q);

// ─── Printers ───────────────────────────────
export type PrinterKind = 'LAN' | 'BLUETOOTH' | 'USB';
export type PrinterConfig = {
  id: string;
  establishmentId: string;
  name: string;
  kind: PrinterKind;
  ipAddress: string | null;
  port: number | null;
  btAddress: string | null;
  target: 'RECEIPT' | 'KITCHEN' | 'BAR';
  isActive: boolean;
  createdAt: string;
};
export const listPrinters = () => get<PrinterConfig[]>('/api/admin/printers');
export const createPrinter = (b: Partial<PrinterConfig>) =>
  post<PrinterConfig>('/api/admin/printers', b);
export const updatePrinter = (id: string, b: Partial<PrinterConfig>) =>
  patch<PrinterConfig>(`/api/admin/printers/${id}`, b);
export const deletePrinter = (id: string) => del(`/api/admin/printers/${id}`);

// ─── Devices (кассы и телефоны официантов) ───────────────────
export type DeviceRole = 'KASSA' | 'WAITER';
export type DeviceRow = {
  id: string;
  name: string;
  kind: 'IPAD' | 'IPHONE' | 'WEB' | 'OTHER';
  role?: DeviceRole;
  // Чей телефон официанта; у кассы — null.
  waiter?: { id: string; name: string | null; email: string | null; status: string } | null;
  lastSeenAt: string | null;
  online: boolean;
  lastIp: string | null;
  userAgent: string | null;
  // Здоровье кассы (п.4/13): версия приложения и размер офлайн-очереди
  // неотправленных операций. null — касса ещё не шлёт heartbeat.
  appVersion?: string | null;
  pendingOps?: number | null;
  revoked: boolean;
  revokedAt: string | null;
  revokedReason: string | null;
  createdAt: string;
};
export type PairCodeResponse = {
  id: string;
  code: string;
  suggestedName: string;
  role?: DeviceRole;
  expiresAt: string;
  expiresInSeconds: number;
};
export const listDevices = (role: DeviceRole = 'KASSA') =>
  get<{ results: DeviceRow[] }>('/api/admin/devices', { role });
export const createDevicePairCode = (suggestedName?: string) =>
  post<PairCodeResponse>('/api/admin/devices/pair-codes', { suggestedName });
// Код для приложения официанта: waiterId — уже заведённый официант, name — новый.
export const createWaiterPairCode = (target: { waiterId: string } | { name: string }) =>
  post<PairCodeResponse>(
    '/api/admin/devices/pair-codes',
    'waiterId' in target
      ? { role: 'WAITER', waiterId: target.waiterId }
      : { role: 'WAITER', suggestedName: target.name },
  );
export const revokeDevice = (id: string, reason?: string) =>
  post(`/api/admin/devices/${id}/revoke`, { reason });

// ─── Модуль «Официанты» (500 ₽/мес к подписке) ───────────────
// off — не подключён; on — работает и продлевается; ending — отключён и
// работает до endsAt (или выдан бесплатно до конца периода).
export type WaitersModuleState = 'off' | 'on' | 'ending';
export type WaitersModuleStatus = {
  state: WaitersModuleState;
  endsAt: string | null;
  free: boolean;
  priceMonthKopecks: number;
  billingPeriod: 'MONTHLY' | 'YEARLY';
  pricePeriodKopecks: number;
  amountWithoutKopecks: number;
  amountWithKopecks: number;
  /** С какой даты модуль платный; null — со следующей оплаты. */
  paidFrom: string | null;
  subscriptionStatus: 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED' | null;
  canManage: boolean;
};
export const getWaitersModule = () => get<WaitersModuleStatus>('/api/admin/modules/waiters');
export const enableWaitersModule = () =>
  post<WaitersModuleStatus>('/api/admin/modules/waiters', {});
export const disableWaitersModule = () => del<WaitersModuleStatus>('/api/admin/modules/waiters');

// ─── Uploads ───────────────────────────────
export type UploadResult = {
  url: string;
  size: number;
  originalSize?: number;
  mime: string;
  width?: number;
  height?: number;
};
export async function uploadImage(
  kind: 'logo' | 'qr' | 'dish' | 'icon' | 'cover',
  file: File,
): Promise<UploadResult> {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post<UploadResult>(`/api/admin/uploads/${kind}`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}
